import { useEffect, useState } from 'react'
import { api } from '../api.js'

export default function Dossier({ id, onRetour, onOuvrir, contexte }) {
  const [d, setD] = useState(null)
  const [motif, setMotif] = useState('')
  const [action, setAction] = useState(null)
  const [erreur, setErreur] = useState('')
  const [confier, setConfier] = useState(null)
  const [voisins, setVoisins] = useState(null)

  const charger = () => api.request(id).then(setD).catch((e) => setErreur(e.message))
  useEffect(() => { charger() }, [id])   // eslint-disable-line

  // Enchaîner sans repasser par la liste : c'est ce qui coûtait le plus cher
  // quand on relève une dizaine de signalements le lundi matin.
  useEffect(() => {
    api.voisins(id, contexte?.statut, contexte?.q).then(setVoisins).catch(() => setVoisins(null))
  }, [id, contexte?.statut, contexte?.q])   // eslint-disable-line

  useEffect(() => {
    function touche(e) {
      if (e.target.matches('input, textarea, select')) return
      if (e.key === 'j' && voisins?.suivant) onOuvrir?.(voisins.suivant)
      if (e.key === 'k' && voisins?.precedent) onOuvrir?.(voisins.precedent)
      if (e.key === 'Escape') onRetour()
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [voisins, onOuvrir, onRetour])

  if (erreur) return <p className="erreur">{erreur}</p>
  if (!d) return <p className="chargement">Chargement…</p>

  async function lancer(a) {
    if ((a.require_comment || a.type === 'ask_user') && !motif.trim()) { setAction(a); return }
    try {
      await api.action(id, a.id, motif)
      setMotif(''); setAction(null); charger()
    } catch (e) { setErreur(e.message) }
  }

  const actionsDisponibles = d.peut_traiter !== false && d.actions.length > 0

  return (
    <>
      <div className="fil">
        <button className="retour" onClick={onRetour}>← Retour aux dossiers</button>
        {voisins?.total > 1 && (
          <div className="navigation">
            <span className="position">{voisins.position} sur {voisins.total}</span>
            <button className="btn sm" disabled={!voisins.precedent} title="Touche K"
                    onClick={() => onOuvrir?.(voisins.precedent)}>← Précédent</button>
            <button className="btn sm" disabled={!voisins.suivant} title="Touche J"
                    onClick={() => onOuvrir?.(voisins.suivant)}>Suivant →</button>
          </div>
        )}
      </div>

      {d.alerts?.map((a, i) => <div className="alerte" key={i} role="status">{a}</div>)}

      <header className="cartouche">
        <p className="ref">DOSSIER N° {d.number} · déposé le {new Date(d.updated_at).toLocaleDateString('fr-FR')}</p>
        <h1>{d.title}</h1>
        <p className="meta">
          <span>Demandeur <strong>{d.applicant?.name}</strong></span>
          <span>Courriel <strong>{d.applicant?.email || '-'}</strong></span>
        </p>
        <span className="tampon">{d.status}</span>
      </header>

      <div className="deux">
        <section>
          {d.awaiting_detail && (
            <div className="carte attente-bloc">
              <h2>En attente de l'habitant</h2>
              <p>{d.awaiting_detail.question}</p>
            </div>
          )}

          <div className="carte">
            <h2>Ce qui a été transmis</h2>
            <table className="recap">
              <tbody>
                {d.fields.map((f) => (
                  <tr key={f.varname}>
                    <th scope="row">{f.label}</th>
                    <td>{Array.isArray(d.data[f.varname]) ? d.data[f.varname].join(', ')
                      : d.data[f.varname] || <em>non renseigné</em>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="carte">
            <h2>Registre du dossier</h2>
            <ol className="journal">
              {[...d.events].reverse().map((e, i) => (
                <li key={i}>
                  <time>{new Date(e.at).toLocaleString('fr-FR')}</time>
                  <strong>{e.title}</strong>{!e.public && <span className="interne">interne</span>}
                  {e.body && <p>{e.body}</p>}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <aside>
          <div className="carte">
            <h2>Qui s'en occupe</h2>
            <dl className="infos-liste">
              <div><dt>Service</dt><dd>{d.service || <em>aucun</em>}</dd></div>
              <div><dt>Suivi par</dt><dd>{d.assignee || <em>personne en particulier</em>}</dd></div>
            </dl>
            {d.read_scope === 'service' && (
              <p className="aide">Ce dossier n'est visible que du service qui le suit.</p>
            )}
            {d.peut_traiter !== false && (
              <button className="btn" onClick={async () => {
                setConfier(await api.handoverOptions(id))
              }}>Confier à…</button>
            )}
            {d.peut_traiter !== false && !d.anonymized_at && (
              <button className="btn dgr" onClick={async () => {
                if (!confirm("Effacer les données personnelles de ce dossier ?\n\n"
                             + "Le dossier restera compté dans vos statistiques, sans identifier "
                             + "personne. L'opération est définitive.")) return
                try {
                  const r = await api.effacerDossier(id, "Demande d'effacement de l'habitant")
                  setErreur(''); charger()
                  alert(r.message)
                } catch (e) { setErreur(e.message) }
              }}>Effacer à la demande de l'habitant</button>
            )}
            {d.anonymized_at && (
              <p className="aide">Données personnelles effacées le
                {' '}{new Date(d.anonymized_at).toLocaleDateString('fr-FR')}.</p>
            )}
            {d.peut_traiter !== false && !d.anonymized_at && (
              d.hold
                ? <>
                    <p className="avertissement">Conservation prolongée : {d.hold_reason}</p>
                    <button className="btn" onClick={async () => {
                      await api.gelerDossier(id, false, '')
                      charger()
                    }}>Lever la conservation prolongée</button>
                  </>
                : <button className="btn" onClick={async () => {
                    const motif = prompt("Pourquoi ce dossier doit-il être conservé au-delà de "
                      + "sa durée habituelle ?\n\nPar exemple : recours en cours, demande du "
                      + "tribunal, litige avec l'entreprise.")
                    if (!motif) return
                    try { await api.gelerDossier(id, true, motif); setErreur(''); charger() }
                    catch (e) { setErreur(e.message) }
                  }}>Conserver au-delà de l'échéance</button>
            )}
          </div>

          <div className="carte">
            <h2>Actions</h2>
            {d.help && <p className="aide">{d.help}</p>}
            {action && (
              <div className="champ">
                <label htmlFor="motif">
                  {action.type === 'ask_user' ? "Que demandez-vous à l'habitant ?" : 'Motif transmis'}
                </label>
                <textarea id="motif" rows={3} value={motif} autoFocus
                          onChange={(e) => setMotif(e.target.value)} />
                <button className="btn pri" onClick={() => lancer(action)}>{action.label}</button>
                <button className="btn" onClick={() => { setAction(null); setMotif('') }}>Annuler</button>
              </div>
            )}
            {d.peut_traiter === false && (
              <p className="aide">Ce dossier est suivi par quelqu'un d'autre. Vous pouvez le
                consulter, pas le faire avancer.</p>
            )}
            {!action && d.peut_traiter !== false && (d.actions.length
              ? d.actions.map((a) => (
                  <button key={a.id} className={`btn large ${a.tone === 'pri' ? 'pri' : a.tone === 'dgr' ? 'dgr' : ''}`}
                          onClick={() => lancer(a)}>{a.label}</button>
                ))
              : <p className="aide">Aucune action disponible à cette étape.</p>)}
          </div>

          <div className="carte">
            <h2>Envois</h2>
            {d.outbox.length ? d.outbox.map((m, i) => (
              <details key={i}>
                <summary>{m.subject}</summary>
                <p className="mono petit">{m.to} · {m.sent ? 'envoyé' : m.error || 'en attente'}</p>
                <pre>{m.body}</pre>
              </details>
            )) : <p className="aide">Rien n'est encore parti.</p>}
          </div>
        </aside>
      </div>

      {/* Sur mobile, les boutons d'action restent sous le pouce plutôt qu'au
          bout d'un long défilement. */}
      {actionsDisponibles && !action && (
        <div className="barre-actions sur-mobile">
          {d.actions.slice(0, 2).map((a) => (
            <button key={a.id} className={`btn ${a.tone === 'pri' ? 'pri' : a.tone === 'dgr' ? 'dgr' : ''}`}
                    onClick={() => lancer(a)}>{a.label}</button>
          ))}
          {d.actions.length > 2 && (
            <button className="btn" onClick={() => {
              document.querySelector('.carte .btn.large')?.scrollIntoView({ behavior: 'smooth' })
            }}>Autres…</button>
          )}
        </div>
      )}

      {confier && (
        <Confier options={confier} onFermer={() => setConfier(null)}
                 onValider={async (payload) => {
                   try {
                     const r = await api.handover(id, payload)
                     setConfier(null); setErreur(''); charger()
                   } catch (e) { setErreur(e.message) }
                 }} />
      )}
    </>
  )
}

// Confier un dossier : à une équipe pour qu'il ne dorme pas pendant un congé,
// ou à une personne quand quelqu'un doit s'en charger nommément.
function Confier({ options, onFermer, onValider }) {
  const [service, setService] = useState(options.actuel.service || '')
  const [agent, setAgent] = useState(options.actuel.assignee_id || '')
  const [commentaire, setCommentaire] = useState('')

  return (
    <div className="fenetre" role="dialog" aria-label="Confier le dossier">
      <div className="fenetre-corps">
        <h2>Confier ce dossier</h2>
        <div className="champ">
          <label htmlFor="c-svc">À quel service</label>
          <select id="c-svc" value={service} onChange={(e) => setService(e.target.value)}>
            <option value="">Aucun service en particulier</option>
            {options.services.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <p className="aide">Toute l'équipe pourra le traiter, même en cas d'absence.</p>
        </div>
        <div className="champ">
          <label htmlFor="c-agent">À quelqu'un en particulier</label>
          <select id="c-agent" value={agent} onChange={(e) => setAgent(e.target.value)}>
            <option value="">Personne en particulier</option>
            {options.agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <p className="aide">Si vous désignez quelqu'un, lui seul pourra faire avancer le
            dossier, avec les responsables.</p>
        </div>
        <div className="champ">
          <label htmlFor="c-note">Un mot pour expliquer (facultatif)</label>
          <input id="c-note" value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
                 placeholder="Relève plutôt de votre service" />
        </div>
        <div className="ligne separee">
          <div className="pousse" />
          <button className="btn" onClick={onFermer}>Annuler</button>
          <button className="btn pri" onClick={() => onValider({
            service_key: service, assignee_id: agent, comment: commentaire })}>Confier</button>
        </div>
      </div>
    </div>
  )
}
